/**
 * GLI INDICI (E IL PETROLIO): NAS100, US30, WTI. Funzione su misura.
 *
 * Esistono solo per i tenant con la capacita' `indices` attiva
 * (`apps/backend/src/lib/capabilities.ts`): questo file dice COME si chiamano
 * e come si riconoscono sul server, non CHI li puo' usare. Decide sempre il
 * backend, con `capabilityActive`; il frontend li mostra solo se `/auth/me`
 * dice che la capacita' e' attiva.
 *
 * Non stanno in `CANONICAL_SYMBOLS`: quella lista la legge la mappatura
 * automatica di TUTTI i tenant, e aggiungerli li' li avrebbe fatti comparire
 * anche a chi non ha la capacita'.
 *
 * I NOMI. Ogni broker chiama lo stesso indice in modo diverso: il canonico e'
 * il nome del gestionale, gli alias sono i nomi comuni sul server. La ricerca
 * e' la stessa di `matchCanonical` (nome esatto, poi alias + separatore +
 * suffisso, `hasSeparatedSuffix`), fatta su ogni alias; un suffisso attaccato
 * (`NAS100m`) non e' un candidato automatico.
 */
import { hasSeparatedSuffix, normalizeSymbolName } from "./canonical-symbols.js";
export const INDEX_SYMBOLS = ["NAS100", "US30", "WTI"];
/** I nomi comuni sul server, il canonico per primo. */
export const INDEX_SYMBOL_ALIASES = {
    NAS100: ["NAS100", "USTEC", "US100", "NDX100"],
    US30: ["US30", "DJ30", "WS30", "DOW30"],
    WTI: ["WTI", "USOIL", "XTIUSD"],
};
export function isIndexSymbol(canonical) {
    return INDEX_SYMBOLS.includes(canonical.trim().toUpperCase());
}
/**
 * L'ALIAS da cui comincia un nome del server, se il nome e' un alias esatto o
 * un alias + separatore + suffisso (`USTEC-E` -> `USTEC`). Null = il nome non
 * e' (automaticamente) questo indice. Il piu' lungo vince: `US100` prima di
 * un ipotetico `US10`.
 */
export function indexAliasOf(canonical, brokerSymbol) {
    const c = canonical.trim().toUpperCase();
    if (!isIndexSymbol(c))
        return null;
    const nome = brokerSymbol.trim().toUpperCase();
    const alias = [...INDEX_SYMBOL_ALIASES[c]].sort((a, b) => b.length - a.length);
    return alias.find((a) => nome === a || hasSeparatedSuffix(nome, a)) ?? null;
}
/** La lista larga della scelta MANUALE: come `looksLikeCanonical`, su ogni alias. */
export const looksLikeIndex = (candidate, canonical) => {
    const c = canonical.trim().toUpperCase();
    if (!isIndexSymbol(c))
        return false;
    const nome = normalizeSymbolName(candidate);
    return INDEX_SYMBOL_ALIASES[c].some((a) => nome.startsWith(normalizeSymbolName(a)));
};
/**
 * I nomi di un indice fra quelli che un conto vede. Un alias solo: i suoi nomi
 * (esatto e varianti) sono le righe del catalogo. Due alias diversi sono due
 * strumenti possibili, e la scelta e' di una persona, come per `XAUUSD` con
 * piu' candidati e nessun nome esatto.
 */
export function matchIndexNames(canonical, names) {
    const perAlias = new Map();
    for (const n of names) {
        const a = indexAliasOf(canonical, n);
        if (a)
            perAlias.set(a, [...(perAlias.get(a) ?? []), n]);
    }
    if (perAlias.size === 0)
        return { kind: "NOT_FOUND" };
    if (perAlias.size > 1)
        return { kind: "AMBIGUOUS", candidates: [...perAlias.values()].flat().sort() };
    const [[alias, trovati]] = [...perAlias.entries()];
    return { kind: "FOUND", alias, names: [...trovati].sort() };
}
//# sourceMappingURL=index-symbols.js.map